import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { FilterDropdown } from "/imports/ui/components/shared";
import { SECTION_LABEL_SX, CARD_SX, AssignedResourcePanel, ALL_DURATIONS } from "/imports/ui/components/booking";

function CourtFilterSidebar({ court }) {
  const {
    currency,
    filters,
    setFilter,
    setFilters,
    availableSports,
    sportFilteredServices,
    availableFacilities,
    allowCourtPick,
    durationOptions,
    serviceLocations,
    canApplyPreferences,
    applyPreferences,
    handleSportChange
  } = court;

  // Pack mode: sport/service/duration are pinned by the variant — show what's
  // being composed instead of dropdowns the pin effect would immediately revert.
  const packMode = court.pack?.active;
  const pinned = packMode || court.reschedule?.active;

  return (
    <Box sx={{ width: "17.5rem", flexShrink: 0, position: "sticky", top: 88 }}>
      <Paper elevation={0} sx={{ ...CARD_SX, p: 2.5, display: "flex", flexDirection: "column", gap: 2.25 }}>
        {packMode && court.pack.resolved && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Booking package</Typography>
            <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>{court.pack.pkg.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {court.pack.opt.sessions} × {court.pack.opt.sessionDuration} min · {court.pack.opt.linkedServiceName || ""}
            </Typography>
          </Box>
        )}

        {!pinned && canApplyPreferences && (
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<AutoAwesomeIcon sx={{ fontSize: "1rem" }} />}
            onClick={applyPreferences}
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          >
            Use my preferences
          </Button>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Sport</Typography>
            <FilterDropdown
              options={availableSports.map((s) => ({ value: s._id, label: s.name }))}
              value={filters.sport}
              onChange={(val) => handleSportChange(val)}
              allowAll={false}
              active={!!filters.sport}
              fullWidth
            />
          </Box>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Service</Typography>
            <FilterDropdown
              options={sportFilteredServices.map((s) => ({ value: s._id, label: s.name }))}
              value={filters.serviceId}
              onChange={(val) => setFilters({ serviceId: val, facilityId: null, duration: "" })}
              allowAll={false}
              active={!!filters.serviceId}
              fullWidth
            />
          </Box>
        )}

        {serviceLocations.length > 0 && (!packMode || !court.pack.packLocationId) && (!court.reschedule?.active || serviceLocations.length > 1) && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Location</Typography>
            <FilterDropdown
              options={serviceLocations.map((l) => ({ value: l._id, label: l.name }))}
              value={filters.locationId}
              onChange={(val) => setFilter("locationId", val)}
              allowAll={serviceLocations.length > 1}
              allLabel="All Locations"
              active={!!filters.locationId}
              fullWidth
            />
          </Box>
        )}

        {!pinned && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Duration</Typography>
            <FilterDropdown
              options={[
                // Several lengths: "All" lists every time and defers the length
                // to the picker — a start hidden for one length still shows.
                ...(durationOptions.length > 1 ? [{ value: ALL_DURATIONS, label: "All durations" }] : []),
                ...durationOptions.map((o) => ({
                  value: String(o.value),
                  label: o.price ? `${o.value} min — ${o.isFrom ? "from " : ""}${(currency || "").toUpperCase()} ${o.price}` : `${o.value} min`
                }))
              ]}
              value={filters.duration}
              onChange={(val) => setFilter("duration", val)}
              allowAll={false}
              active={!!filters.duration}
              fullWidth
            />
          </Box>
        )}

        {availableFacilities.length > 0 && (
          <Box>
            <Typography sx={SECTION_LABEL_SX}>Court</Typography>
            {allowCourtPick ? (
              <FilterDropdown
                options={availableFacilities.map((f) => ({ value: f._id, label: f.name }))}
                value={filters.facilityId}
                onChange={(val) => setFilter("facilityId", val)}
                allLabel="Any Court"
                active={!!filters.facilityId}
                fullWidth
              />
            ) : (
              <AssignedResourcePanel noun="Court" names={availableFacilities.map((f) => f.name)} />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
}

export default CourtFilterSidebar;
